// components/ThemeSelector.js - Forbedret tema-velger med mobil scroll
import React, { useState } from 'react';
import { Palette, Check, ChevronDown } from 'lucide-react';
import { useTheme } from '../hooks/useTheme';

const ThemeSelector = () => {
  const { currentTheme, themeInfo, changeTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const handleThemeChange = (themeId) => {
    changeTheme(themeId);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
        aria-label="Velg fargetema"
      >
        <Palette className="w-5 h-5 text-gray-600" />
        <span className="text-sm font-medium text-gray-700 hidden sm:block">
          {themeInfo.find(t => t.id === currentTheme)?.name || 'Tema'}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-500" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-72 sm:w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-[60]">
          {/* Header */}
          <div className="p-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-800">Velg fargetema</h3>
            <p className="text-xs text-gray-600 mt-1">
              Endrer utseendet til hele siden
            </p>
          </div>

          {/* Scrollable Content */}
          <div className="max-h-64 sm:max-h-80 overflow-y-auto p-2 space-y-1">
            {themeInfo.map((theme) => {
              const isSelected = currentTheme === theme.id;

              return (
                <button
                  key={theme.id}
                  onClick={() => handleThemeChange(theme.id)}
                  className={`w-full text-left p-3 rounded-lg transition-all hover:bg-gray-50 ${
                    isSelected ? 'bg-blue-50 border border-blue-200' : 'border border-transparent'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      {/* Tema navn */}
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="text-sm font-medium text-gray-800">
                          {theme.name}
                        </span>
                        {isSelected && (
                          <Check className="w-4 h-4 text-blue-600" />
                        )}
                      </div>
                      
                      {/* Beskrivelse */}
                      <div className="text-xs text-gray-600 mb-3">
                        {theme.description}
                      </div>

                      {/* Fargeforhåndsvisning */}
                      <div className="flex space-x-1">
                        {theme.preview.map((color, index) => (
                          <div
                            key={index}
                            className="w-6 h-6 rounded-full border border-gray-200"
                            style={{ backgroundColor: color }}
                            title={`Farge ${index + 1}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* Footer Info */}
          <div className="p-3 border-t border-gray-100 bg-gray-50">
            <div className="text-xs text-gray-500">
              💡 Ditt valg lagres automatisk
            </div>
          </div>
        </div>
      )}

      {/* Bakgrunnsklikk for å lukke */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

// ALTERNATIV: Kompakt mobil-versjon
export const MobileThemeSelector = () => {
  const { currentTheme, themeInfo, changeTheme } = useTheme();
  const [isOpen, setIsOpen] = useState(false);

  const handleThemeChange = (themeId) => {
    changeTheme(themeId);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      {/* Trigger Button - Kompakt for mobil */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        aria-label="Velg fargetema"
      >
        <Palette className="w-4 h-4 text-gray-600" />
        <span className="text-xs font-medium text-gray-700">
          Tema
        </span>
        <ChevronDown className="w-3 h-3 text-gray-500" />
      </button>

      {/* Fullscreen Modal på mobil, dropdown på desktop */}
      {isOpen && (
        <>
          {/* Mobile: Fullscreen modal */}
          <div className="fixed inset-0 z-[70] bg-black bg-opacity-50 sm:hidden">
            <div className="fixed bottom-0 left-0 right-0 bg-white rounded-t-lg max-h-[80vh] overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">Velg fargetema</h3>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-2 hover:bg-gray-100 rounded-full"
                  >
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  </button>
                </div>
              </div>
              
              <div className="overflow-y-auto p-4 space-y-3" style={{ maxHeight: 'calc(80vh - 80px)' }}>
                {themeInfo.map((theme) => {
                  const isSelected = currentTheme === theme.id;
                  return (
                    <button
                      key={theme.id}
                      onClick={() => handleThemeChange(theme.id)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-800">{theme.name}</span>
                        {isSelected && <Check className="w-5 h-5 text-blue-600" />}
                      </div>
                      <div className="text-sm text-gray-600 mb-3">{theme.description}</div>
                      <div className="flex space-x-2">
                        {theme.preview.map((color, index) => (
                          <div
                            key={index}
                            className="w-8 h-8 rounded-full border border-gray-200"
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Desktop: Dropdown */}
          <div className="hidden sm:block absolute top-full right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-[60]">
            <div className="p-3 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-800">Velg fargetema</h3>
            </div>
            
            <div className="max-h-80 overflow-y-auto p-2 space-y-1">
              {themeInfo.map((theme) => {
                const isSelected = currentTheme === theme.id;
                return (
                  <button
                    key={theme.id}
                    onClick={() => handleThemeChange(theme.id)}
                    className={`w-full text-left p-3 rounded-lg transition-all hover:bg-gray-50 ${
                      isSelected ? 'bg-blue-50 border border-blue-200' : 'border border-transparent'
                    }`}
                  >
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="text-sm font-medium text-gray-800">{theme.name}</span>
                      {isSelected && <Check className="w-4 h-4 text-blue-600" />}
                    </div>
                    <div className="text-xs text-gray-600 mb-3">{theme.description}</div>
                    <div className="flex space-x-1">
                      {theme.preview.map((color, index) => (
                        <div
                          key={index}
                          className="w-6 h-6 rounded-full border border-gray-200"
                          style={{ backgroundColor: color }}
                        />
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Desktop backdrop */}
          <div
            className="hidden sm:block fixed inset-0 z-50"
            onClick={() => setIsOpen(false)}
          />
        </>
      )}
    </div>
  );
};

// ALTERNATIV: Enkle knapper som tar mindre plass
export const SimpleThemeButtons = () => {
  const { currentTheme, themeInfo, changeTheme } = useTheme();

  return (
    <div className="flex flex-wrap gap-2">
      {themeInfo.map((theme) => (
        <button
          key={theme.id}
          onClick={() => changeTheme(theme.id)}
          className={`px-3 py-2 text-xs rounded-lg border transition-colors ${
            currentTheme === theme.id
              ? 'bg-blue-500 text-white border-blue-500'
              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'
          }`}
          title={theme.description}
        >
          {theme.name}
        </button>
      ))}
    </div>
  );
};

// ALTERNATIV: Fargeprøver som knapper
export const ColorSwatchSelector = () => {
  const { currentTheme, themeInfo, changeTheme } = useTheme();

  return (
    <div className="flex flex-wrap gap-2">
      {themeInfo.map((theme) => (
        <button
          key={theme.id}
          onClick={() => changeTheme(theme.id)}
          className={`w-8 h-8 rounded-full border-2 transition-all hover:scale-110 ${
            currentTheme === theme.id ? 'border-gray-800' : 'border-gray-300'
          }`}
          style={{ backgroundColor: theme.preview[0] }}
          title={`${theme.name} - ${theme.description}`}
        />
      ))}
    </div>
  );
};

export default ThemeSelector;